import { useEffect, useRef, useState, useCallback } from 'react';
import {
	MessageSquare, X, Maximize2, Minimize2, Send, Image as ImageIcon, Trash2,
	Search, Eye, Pencil, Plus, Loader2, Copy, Check, Mic, MicOff,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { api, type AgentMessage, type AgentContent } from '@/lib/api';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { QwenAsr } from '@/lib/asr';

interface ChatMsg {
	role: 'user' | 'assistant';
	content: AgentContent;
	tools?: ToolEvent[];
}
interface ToolEvent {
	name: string;
	args?: any;
	state: 'running' | 'done' | 'error';
	summary?: string;
}

const TOOL_LABELS: Record<string, { label: string; icon: React.ElementType }> = {
	search_properties: { label: '搜索房源', icon: Search },
	get_property: { label: '查看房源', icon: Eye },
	create_property: { label: '新增房源', icon: Plus },
	update_property: { label: '修改房源', icon: Pencil },
	delete_property: { label: '删除房源', icon: Trash2 },
};

const STORAGE_KEY = 'fangyuan.chat.history.v1';

export function ChatFab({ onClick }: { onClick: () => void }) {
	return (
		<button
			onClick={onClick}
			className="fixed bottom-5 right-5 z-50 w-12 h-12 rounded-full bg-accent text-white font-semibold shadow-soft-md hover:bg-accent-2 hover:-translate-y-0.5 transition-all flex items-center justify-center"
			aria-label="打开 AI 助手"
		>
			<MessageSquare size={18} />
		</button>
	);
}

export function ChatWindow({ open, onClose }: { open: boolean; onClose: () => void }) {
	const qc = useQueryClient();
	const [messages, setMessages] = useState<ChatMsg[]>(() => {
		try {
			const raw = sessionStorage.getItem(STORAGE_KEY);
			return raw ? JSON.parse(raw) : [];
		} catch {
			return [];
		}
	});
	const [input, setInput] = useState('');
	const [pendingImages, setPendingImages] = useState<string[]>([]); // base64 data URLs
	const [busy, setBusy] = useState(false);
	const [maximized, setMaximized] = useState(false);
	const [recording, setRecording] = useState(false);
	const scrollerRef = useRef<HTMLDivElement>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const abortRef = useRef<AbortController | null>(null);
	const asrRef = useRef<QwenAsr | null>(null);
	const finalAccRef = useRef('');
	const interimRef = useRef('');

	useEffect(() => {
		try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages)); } catch {}
	}, [messages]);

	useEffect(() => {
		scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight, behavior: 'smooth' });
	}, [messages, busy]);

	const addImagesFromFiles = useCallback(async (files: FileList | File[]) => {
		const arr = Array.from(files).filter((f) => f.type.startsWith('image/'));
		const dataUrls = await Promise.all(arr.map((f) => fileToDataUrl(f)));
		setPendingImages((prev) => [...prev, ...dataUrls]);
	}, []);

	// 粘贴图片支持
	useEffect(() => {
		if (!open) return;
		const onPaste = (e: ClipboardEvent) => {
			const items = Array.from(e.clipboardData?.items ?? []);
			const imgs = items.filter((it) => it.type.startsWith('image/'));
			if (imgs.length === 0) return;
			e.preventDefault();
			const files = imgs.map((it) => it.getAsFile()).filter(Boolean) as File[];
			void addImagesFromFiles(files);
		};
		window.addEventListener('paste', onPaste);
		return () => window.removeEventListener('paste', onPaste);
	}, [open, addImagesFromFiles]);

	// 语音输入（Qwen3-ASR-Flash-Realtime via WebSocket Worker 代理）
	const voiceSupported =
		typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;

	const toggleRecording = useCallback(async () => {
		if (recording) {
			asrRef.current?.stop();
			return;
		}
		if (!voiceSupported) {
			toast.error('当前浏览器不支持麦克风');
			return;
		}
		finalAccRef.current = '';
		interimRef.current = '';
		const baseInput = input ? input + ' ' : '';
		const asr = new QwenAsr({
			onInterim: (txt) => {
				interimRef.current = txt;
				setInput(baseInput + finalAccRef.current + interimRef.current);
			},
			onFinal: (txt) => {
				finalAccRef.current += txt;
				interimRef.current = '';
				setInput(baseInput + finalAccRef.current);
			},
			onError: (msg) => {
				toast.error('语音识别：' + msg);
				setRecording(false);
			},
			onClose: () => setRecording(false),
		});
		asrRef.current = asr;
		setRecording(true);
		try {
			await asr.start();
		} catch {
			setRecording(false);
		}
	}, [recording, voiceSupported, input]);

	useEffect(() => {
		return () => asrRef.current?.stop();
	}, []);

	if (!open) return null;

	const removeImage = (i: number) => setPendingImages((prev) => prev.filter((_, idx) => idx !== i));

	const send = async () => {
		const text = input.trim();
		if ((!text && pendingImages.length === 0) || busy) return;

		const userContent: AgentContent =
			pendingImages.length > 0
				? [
						...pendingImages.map((url) => ({ type: 'image_url' as const, image_url: { url } })),
						...(text ? [{ type: 'text' as const, text }] : []),
					]
				: text;

		const userMsg: ChatMsg = { role: 'user', content: userContent };
		const newMessages = [...messages, userMsg];
		setMessages(newMessages);
		setInput('');
		setPendingImages([]);
		setBusy(true);

		// 准备一个空 assistant 消息，用 ref 累积内容
		const assistantIdx = newMessages.length;
		setMessages((m) => [...m, { role: 'assistant', content: '', tools: [] }]);
		let acc = '';
		let toolEvents: ToolEvent[] = [];
		let touchedDB = false;

		try {
			const apiMessages: AgentMessage[] = newMessages.map((m) => ({
				role: m.role,
				content: m.content,
			}));
			abortRef.current = new AbortController();
			const res = await api.agent(apiMessages, abortRef.current.signal);
			const reader = res.body!.getReader();
			const dec = new TextDecoder();
			let buf = '';
			while (true) {
				const { value, done } = await reader.read();
				if (done) break;
				buf += dec.decode(value, { stream: true });
				const lines = buf.split('\n');
				buf = lines.pop() ?? '';
				for (const line of lines) {
					const trimmed = line.trim();
					if (!trimmed.startsWith('data:')) continue;
					const payload = trimmed.slice(5).trim();
					if (!payload) continue;
					let evt: any;
					try { evt = JSON.parse(payload); } catch { continue; }

					if (evt.type === 'text' && evt.delta) {
						acc += evt.delta;
						updateAssistant(assistantIdx, acc, toolEvents);
					} else if (evt.type === 'tool_use') {
						toolEvents = [...toolEvents, { name: evt.name, args: evt.args, state: 'running' }];
						updateAssistant(assistantIdx, acc, toolEvents);
					} else if (evt.type === 'tool_result') {
						const i = toolEvents.findIndex((t) => t.name === evt.name && t.state === 'running');
						if (i >= 0) {
							toolEvents = toolEvents.map((t, idx) =>
								idx === i ? { ...t, state: evt.ok ? 'done' : 'error', summary: evt.summary } : t
							);
							updateAssistant(assistantIdx, acc, toolEvents);
						}
						if (evt.ok && ['create_property', 'update_property', 'delete_property'].includes(evt.name)) {
							touchedDB = true;
						}
					} else if (evt.type === 'error') {
						toast.error('AI 出错：' + evt.message);
					}
				}
			}
		} catch (err) {
			if ((err as Error).name !== 'AbortError') {
				toast.error('网络出错：' + (err as Error).message);
			}
		} finally {
			setBusy(false);
			abortRef.current = null;
			if (touchedDB) {
				qc.invalidateQueries({ queryKey: ['properties'] });
				qc.invalidateQueries({ queryKey: ['stats'] });
			}
		}

		function updateAssistant(idx: number, content: string, tools: ToolEvent[]) {
			setMessages((m) => {
				const next = [...m];
				next[idx] = { role: 'assistant', content, tools };
				return next;
			});
		}
	};

	const stop = () => abortRef.current?.abort();
	const clearChat = () => {
		setMessages([]);
		setPendingImages([]);
		try { sessionStorage.removeItem(STORAGE_KEY); } catch {}
	};

	return (
		<div
			className={cn(
				'fixed z-50 bg-white border border-line shadow-soft-lg flex flex-col overflow-hidden transition-all',
				// 手机：默认就全屏
				'inset-0 rounded-none',
				// 平板/桌面：浮窗
				maximized
					? 'md:inset-6 md:rounded-xl'
					: 'md:bottom-20 md:right-5 md:top-auto md:left-auto md:w-[520px] md:h-[660px] md:max-w-[calc(100vw-40px)] md:max-h-[calc(100vh-100px)] md:rounded-xl'
			)}
		>
			{/* Header */}
			<div className="px-3.5 py-2.5 border-b border-line bg-[#fafafa] flex items-center justify-between">
				<h3 className="text-[16px] font-semibold flex items-center gap-1.5">
					<MessageSquare size={14} className="text-accent" />
					AI 助手
					<span className="text-[12.5px] text-ink-3 font-normal ml-1">qwen3.6-plus</span>
				</h3>
				<div className="flex gap-0.5">
					<button onClick={clearChat} className="icon-btn" title="清空对话"><Trash2 size={13} /></button>
					<button onClick={() => setMaximized((v) => !v)} className="icon-btn" title={maximized ? '还原' : '最大化'}>
						{maximized ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
					</button>
					<button onClick={onClose} className="icon-btn"><X size={14} /></button>
				</div>
			</div>

			{/* Messages */}
			<div ref={scrollerRef} className="flex-1 overflow-y-auto p-3.5 flex flex-col gap-3 scrollbar-thin">
				{messages.length === 0 && (
					<div className="text-center text-ink-3 text-[15px] mt-8 px-4">
						<MessageSquare size={32} className="mx-auto mb-3 opacity-40" />
						<div className="font-medium text-ink-2 mb-1">问我任何房源相关的事</div>
						<div className="text-[14px] leading-relaxed mt-3 space-y-1.5">
							<Suggestion text="帮我看下滨江豪庭都有哪些房" />
							<Suggestion text="把 F602 改成已售" />
							<Suggestion text="汇总下本月新增的房源" />
							<Suggestion text="📷 上传房源截图，我帮你提取信息" />
						</div>
					</div>
				)}
				{messages.map((m, i) => (
					<MessageBubble key={i} msg={m} />
				))}
				{busy && messages[messages.length - 1]?.role === 'user' && (
					<div className="flex items-center gap-2 text-ink-3 text-[14px]">
						<Loader2 size={12} className="animate-spin" /> 思考中...
					</div>
				)}
			</div>

			{/* Pending images preview */}
			{pendingImages.length > 0 && (
				<div className="px-3.5 py-2 border-t border-line bg-[#fafafa] flex gap-1.5 overflow-x-auto scrollbar-thin">
					{pendingImages.map((url, i) => (
						<div key={i} className="relative flex-shrink-0">
							<img src={url} alt="" className="w-14 h-14 object-cover rounded-md border border-line" />
							<button
								onClick={() => removeImage(i)}
								className="absolute -top-1 -right-1 w-4 h-4 bg-ink text-white rounded-full text-[12px] flex items-center justify-center hover:bg-danger"
							>
								<X size={9} />
							</button>
						</div>
					))}
				</div>
			)}

			{/* Input */}
			<div className="px-3 py-2.5 border-t border-line flex items-end gap-1.5">
				<button
					onClick={() => fileInputRef.current?.click()}
					className="icon-btn flex-shrink-0"
					title="上传图片"
					disabled={busy}
				>
					<ImageIcon size={15} />
				</button>
				{voiceSupported && (
					<button
						onClick={toggleRecording}
						className={cn(
							'icon-btn flex-shrink-0',
							recording && '!bg-red-50 !text-danger animate-pulse'
						)}
						title={recording ? '停止录音' : '语音输入'}
						disabled={busy && !recording}
					>
						{recording ? <MicOff size={15} /> : <Mic size={15} />}
					</button>
				)}
				<input
					ref={fileInputRef}
					type="file"
					accept="image/*"
					multiple
					hidden
					onChange={(e) => {
						if (e.target.files) void addImagesFromFiles(e.target.files);
						e.target.value = '';
					}}
				/>
				<textarea
					value={input}
					onChange={(e) => setInput(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
							e.preventDefault();
							send();
						}
					}}
					placeholder="问点关于房源的问题，按 Enter 发送，Shift+Enter 换行..."
					disabled={busy}
					rows={1}
					className="flex-1 bg-[#fafafa] border border-line rounded-md px-2.5 py-1.5 text-[15.5px] focus:outline-none focus:border-accent disabled:opacity-50 resize-none max-h-32 leading-snug"
					style={{ minHeight: 32 }}
				/>
				{busy ? (
					<Button variant="danger" size="sm" onClick={stop}>停止</Button>
				) : (
					<Button
						variant="primary"
						size="sm"
						onClick={send}
						disabled={!input.trim() && pendingImages.length === 0}
						className="gap-1"
					>
						<Send size={12} />
					</Button>
				)}
			</div>
		</div>
	);
}

function Suggestion({ text }: { text: string }) {
	return <div className="px-2 py-1 inline-block bg-[#f4f4f3] rounded-md mr-1">{text}</div>;
}

function MessageBubble({ msg }: { msg: ChatMsg }) {
	const [copied, setCopied] = useState(false);
	const isUser = msg.role === 'user';
	const textContent = extractText(msg.content);
	const images = extractImages(msg.content);

	const copy = async () => {
		try {
			await navigator.clipboard.writeText(textContent);
			setCopied(true);
			setTimeout(() => setCopied(false), 1500);
		} catch {}
	};

	return (
		<div className={cn('flex flex-col group', isUser ? 'items-end' : 'items-start')}>
			{/* Tool chips (assistant only, above message) */}
			{!isUser && msg.tools && msg.tools.length > 0 && (
				<div className="flex flex-wrap gap-1 mb-1.5 max-w-[90%]">
					{msg.tools.map((t, i) => <ToolChip key={i} tool={t} />)}
				</div>
			)}

			<div
				className={cn(
					'rounded-xl text-[15.5px] leading-relaxed relative',
					// assistant 回答可以撑到 96%（容纳表格），user 消息保持 88%
					isUser ? 'max-w-[88%] bg-accent text-white px-3 py-2 rounded-br-sm' : 'max-w-[96%] w-fit bg-[#f4f4f3] text-ink px-3.5 py-2.5 rounded-bl-sm'
				)}
			>
				{images.length > 0 && (
					<div className="flex flex-wrap gap-1.5 mb-1.5">
						{images.map((url, i) => (
							<img key={i} src={url} alt="" className="max-w-[180px] max-h-[180px] rounded-md object-cover border border-white/20" />
						))}
					</div>
				)}
				{textContent && (
					isUser ? (
						<div className="whitespace-pre-wrap">{textContent}</div>
					) : (
						<div className="prose prose-base max-w-none prose-headings:my-2 prose-p:my-1.5 prose-ul:my-1.5 prose-li:my-0 prose-pre:my-2 prose-pre:bg-white prose-pre:border prose-pre:border-line prose-pre:p-2 prose-pre:text-[14px] prose-code:text-[14.5px] prose-code:before:content-none prose-code:after:content-none prose-code:bg-white prose-code:px-1 prose-code:py-0.5 prose-code:rounded">
							<ReactMarkdown
								remarkPlugins={[remarkGfm]}
								components={{
									a: (props) => <a {...props} target="_blank" rel="noopener noreferrer" />,
									table: (props) => (
										<div
											className="overflow-x-auto -mx-3.5 px-3.5 my-2 scrollbar-thin relative"
											style={{
												// 右侧渐隐提示「可以横滑」
												maskImage: 'linear-gradient(to right, black calc(100% - 12px), transparent)',
												WebkitMaskImage: 'linear-gradient(to right, black calc(100% - 12px), transparent)',
											}}
										>
											<table
												{...props}
												className="border-collapse text-[14px] w-auto leading-tight bg-white"
												style={{ tableLayout: 'auto' }}
											/>
										</div>
									),
									thead: (props) => <thead {...props} className="bg-[#fafafa]" />,
									tr: (props) => <tr {...props} className="border-b border-line last:border-0" />,
									th: (props) => (
										<th
											{...props}
											className="border-r border-line text-ink font-semibold px-2 py-1.5 text-left whitespace-nowrap text-[13px] uppercase tracking-wide last:border-0"
										/>
									),
									td: (props) => (
										<td
											{...props}
											className="border-r border-line px-2 py-1.5 align-top last:border-0"
											style={{ wordBreak: 'break-word', maxWidth: 220 }}
										/>
									),
								}}
							>
								{textContent}
							</ReactMarkdown>
						</div>
					)
				)}
			</div>

			{/* Action bar (only assistant + has text) */}
			{!isUser && textContent && (
				<button
					onClick={copy}
					className={cn(
						'mt-1 inline-flex items-center gap-1 text-[13px] text-ink-3 hover:text-ink',
						'opacity-0 group-hover:opacity-100 transition-opacity'
					)}
				>
					{copied ? <Check size={10} /> : <Copy size={10} />}
					{copied ? '已复制' : '复制'}
				</button>
			)}
		</div>
	);
}

function ToolChip({ tool }: { tool: ToolEvent }) {
	const meta = TOOL_LABELS[tool.name] ?? { label: tool.name, icon: Loader2 };
	const Icon = meta.icon;
	return (
		<div
			className={cn(
				'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[13.5px] border',
				tool.state === 'running' && 'bg-blue-50 border-blue-100 text-blue-700',
				tool.state === 'done' && 'bg-accent-soft border-accent/20 text-accent',
				tool.state === 'error' && 'bg-red-50 border-red-100 text-danger'
			)}
		>
			{tool.state === 'running' ? <Loader2 size={10} className="animate-spin" /> : <Icon size={10} />}
			<span>{meta.label}</span>
			{tool.summary && <span className="text-ink-3">· {tool.summary}</span>}
		</div>
	);
}

function extractText(c: AgentContent): string {
	if (typeof c === 'string') return c;
	return c.filter((p) => p.type === 'text').map((p) => (p as any).text).join('');
}
function extractImages(c: AgentContent): string[] {
	if (typeof c === 'string') return [];
	return c.filter((p) => p.type === 'image_url').map((p) => (p as any).image_url.url);
}

function fileToDataUrl(file: File): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(reader.result as string);
		reader.onerror = reject;
		reader.readAsDataURL(file);
	});
}
